import { computed, ref, watch, onMounted, onScopeDispose, type Ref } from 'vue';
import { useAuthState } from '~/composables/useAuthState';
import { copyrightService, type CopyrightConfig, type CertificateState } from '~/services/copyrightService';
import type { MusicTask } from '~/services/musicService';

// Create and Library share status labels; the certificate page keeps purchase eligibility.
export function useTaskCertificates(
  tasks: Ref<MusicTask[]>,
  auth = useAuthState(),
  service = copyrightService,
  scope: 'song' | 'task' = 'song',
) {
  const copyrightConfig = ref<CopyrightConfig | null>(null);
  const states = ref<Record<string, CertificateState>>({});
  let configRequest = 0;
  let stateRequest = 0;
  let disposed = false;
  const key = (taskID: string, musicID: string) => JSON.stringify([taskID, musicID]);
  const hasAccess = computed(() => !!copyrightConfig.value?.eligible || Number(copyrightConfig.value?.order_count) > 0);
  const songs = computed(() => tasks.value.filter(task => task.status === 3).flatMap(task => scope === 'task'
    ? (task.music_data || []).some(song => song.id && (song.audio_oss || song.audio_url)) ? [{ task_id: task.id, music_id: '', scope: 'task' as const }] : []
    : (task.music_data || []).map(song => ({ task_id: task.id, music_id: song.id }))));

  const refreshCopyrightConfig = async () => {
    const request = ++configRequest;
    ++stateRequest;
    copyrightConfig.value = null;
    states.value = {};
    if (!auth.isAuthenticated.value || disposed) return;
    try {
      if (!await auth.fetchAndStoreToken() || request !== configRequest || disposed) return;
      const result = await service.config();
      if (request === configRequest && auth.isAuthenticated.value && !disposed) copyrightConfig.value = result;
    } catch { /* Unknown configuration never grants purchase permission. */ }
  };

  const refreshStates = async () => {
    const request = ++stateRequest;
    states.value = {};
    if (!hasAccess.value || !auth.isAuthenticated.value || !songs.value.length || disposed) return;
    const requestedSongs = songs.value;
    try {
      if (!await auth.fetchAndStoreToken() || request !== stateRequest || disposed) return;
      const result: Record<string, CertificateState> = {};
      for (let start = 0; start < requestedSongs.length; start += 100) {
        const group = requestedSongs.slice(start, start + 100);
        const found = await service.states(group);
        if (request !== stateRequest || !auth.isAuthenticated.value || disposed) return;
        group.forEach(song => { result[key(song.task_id, song.music_id)] = { ...song, status: 'none' }; });
        found.forEach(state => { result[key(state.task_id, state.music_id)] = state; });
      }
      if (request === stateRequest && !disposed) states.value = result;
    } catch { /* Keep a neutral entry when a status cannot be verified. */ }
  };

  const taskCertificateStates = (task: MusicTask) => scope === 'task'
    ? states.value[key(task.id, '')] ? { '': states.value[key(task.id, '')].status } : {}
    : Object.fromEntries((task.music_data || [])
    .filter(song => states.value[key(task.id, song.id)])
    .map(song => [song.id, states.value[key(task.id, song.id)].status]));

  watch([auth.isAuthenticated, () => auth.user.value?.id], () => { void refreshCopyrightConfig(); }, { flush: 'sync' });
  watch([() => songs.value.map(song => key(song.task_id, song.music_id)).join('|'), copyrightConfig], () => { void refreshStates(); });
  onMounted(() => { void refreshCopyrightConfig(); });
  onScopeDispose(() => { disposed = true; ++configRequest; ++stateRequest; });
  return { copyrightConfig, taskCertificateStates, refreshCopyrightConfig };
}
